import { useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { usersMessages } from '@/i18n/messages/users'

/** The Users admin's modal frame: header, scrolling body, footer. Click outside or ✕ closes. */
export function Modal({ title, subtitle, onClose, footer, children, wide }: {
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  footer?: ReactNode
  children: ReactNode
  wide?: boolean
}) {
  const tc = useT(commonMessages)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" onClick={onClose}>
      <div className={`flex max-h-[min(90vh,100dvh-2rem)] w-full flex-col rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-neutral-900 dark:ring-white/10 ${wide ? 'max-w-xl' : 'max-w-md'}`}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 border-b border-neutral-200 p-5 dark:border-neutral-800">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{title}</h2>
            {subtitle && <div className="mt-0.5 text-sm text-neutral-500">{subtitle}</div>}
          </div>
          <button onClick={onClose} aria-label={tc('close')}
                  className="grid size-10 shrink-0 place-items-center rounded-xl text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200">✕</button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-5">{children}</div>
        {footer && <div className="flex items-center justify-between gap-3 border-t border-neutral-200 p-5 dark:border-neutral-800">{footer}</div>}
      </div>
    </div>
  )
}

export const btnPrimary = 'rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60'
export const btnSecondary = 'rounded-lg px-3 py-1.5 text-sm text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 disabled:opacity-60 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800'
export const inputCls = 'w-full rounded-lg border-0 bg-white px-2.5 py-1.5 text-sm ring-1 ring-neutral-300 focus:ring-2 focus:ring-brand-500 dark:bg-neutral-950 dark:ring-neutral-700'
export const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-neutral-400'

export function ErrorLine({ error }: { error: string | null }) {
  return error ? <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p> : <span />
}

/** A freshly generated login, shown once: large, monospace, with a copy button. */
export function Credentials({ username, password }: { username?: string | undefined; password: string }) {
  const t = useT(usersMessages)
  const [copied, setCopied] = useState(false)
  const text = username ? `${t('username')}: ${username}\n${t('password')}: ${password}` : password
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch { /* the text stays selectable */ }
  }
  return (
    <div className="space-y-2 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
      <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1">
        {username && <><dt className="text-xs text-neutral-500">{t('username')}</dt><dd className="select-all font-mono text-base">{username}</dd></>}
        <dt className="text-xs text-neutral-500">{t('password')}</dt>
        <dd className="select-all font-mono text-xl tracking-wider">{password}</dd>
      </dl>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-amber-900 dark:text-amber-200">{t('credentialsHint')}</p>
        <button type="button" onClick={copy} className={btnSecondary}>{copied ? t('copied') : t('copy')}</button>
      </div>
    </div>
  )
}

/** A two-step confirmation inside the page (the browser's confirm() isn't used). */
export function ConfirmButton({ label, question, onConfirm, busy, danger }: {
  label: string; question: string; onConfirm: () => void; busy?: boolean; danger?: boolean
}) {
  const t = useT(usersMessages)
  const tc = useT(commonMessages)
  const [asking, setAsking] = useState(false)
  if (!asking)
    return <button type="button" onClick={() => setAsking(true)} disabled={busy}
                   className={danger ? 'rounded-lg px-3 py-1.5 text-sm font-medium text-red-700 ring-1 ring-red-300 hover:bg-red-50 disabled:opacity-60 dark:text-red-300 dark:ring-red-500/40 dark:hover:bg-red-500/10' : btnSecondary}>{label}</button>
  return (
    <div className="w-full space-y-2 rounded-lg bg-neutral-50 p-3 text-sm dark:bg-neutral-800/60">
      <p>{question}</p>
      <div className="flex gap-2">
        <button type="button" onClick={() => { setAsking(false); onConfirm() }} disabled={busy} className={btnPrimary}>{t('confirm')}</button>
        <button type="button" onClick={() => setAsking(false)} className={btnSecondary}>{tc('cancel')}</button>
      </div>
    </div>
  )
}
